import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { ref, push, update, onValue } from "firebase/database";
import { getSessionInfo } from "../roles";

const DOMAIN = "vnrvjiet.in";

function emptyMember() {
  return { roll: "", n: "", b: "", s: "", m: "", noc: false };
}

export default function VolunteerTeams() {
  const vol = useMemo(() => getSessionInfo() || {}, []);
  const [teamName, setTeamName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [member, setMember] = useState(emptyMember());
  const [teams, setTeams] = useState([]);

  // Load only teams created by the volunteer
  useEffect(() => {
    const off = onValue(ref(db, "teams"), (snap) => {
      const v = snap.val() || {};
      const rows = Object.entries(v).map(([id, t]) => ({ id, ...t }));
      setTeams(rows.filter((r) => r.by === vol?.id));
    });
    return () => off();
  }, [vol?.id]);

  async function createTeam() {
    if (!teamName.trim()) {
      alert("Enter team name");
      return;
    }
    const now = Date.now();
    const node = push(ref(db, "teams"));
    await update(ref(db), {
      [`teams/${node.key}`]: {
        n: teamName.trim(),
        by: vol?.id || "unknown",
        ts: now,
        m: {},
      },
      [`audit/teamCreate/${now}_${node.key}`]: {
        id: node.key,
        by: vol?.id || "unknown",
        ts: now,
      },
    });
    setTeamId(node.key);
    setTeamName("");
    alert(`Team created: ${node.key}`);
  }

  async function addMember() {
    if (!teamId) {
      alert("Create/select a team first");
      return;
    }
    if (!member.roll.trim()) {
      alert("Roll number is required");
      return;
    }
    if (!member.m.trim() || member.m.length !== 10) {
      alert("Enter a valid 10-digit mobile number");
      return;
    }

    const key = member.roll.trim().toLowerCase();
    const email = `${key}@${DOMAIN}`;
    const now = Date.now();
    const data = {
      n: member.n || key.toUpperCase(),
      b: member.b || "",
      s: member.s || "",
      e: email,
      m: member.m.trim(),
      noc: member.noc ? true : false,
    };

    const up = {};
    up[`teams/${teamId}/m/${key}`] = data;
    up[`members/${key}`] = { ...data, t: teamId };
    up[`audit/memberAdd/${now}_${teamId}_${key}`] = {
      teamId,
      roll: key,
      by: vol?.id || "unknown",
      ts: now,
    };

    await update(ref(db), up);
    setMember(emptyMember());
    alert(`Member ${key} added successfully.`);
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">
        Volunteer — Add Teams & Participants
      </h1>

      <div className="bg-white rounded-2xl shadow p-6 space-y-4">
        <div className="text-sm text-gray-600">
          Logged in: {vol?.name} ({vol?.id})
        </div>

        {/* Create or select team */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            className="p-3 border rounded-xl"
            placeholder="Team name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
          />
          <button
            onClick={createTeam}
            className="px-4 py-3 rounded-xl bg-black text-white hover:opacity-90"
          >
            Create Team
          </button>

          <select
            className="p-3 border rounded-xl"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            <option value="">Select your team</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.n} ({t.id})
              </option>
            ))}
          </select>
        </div>

        {/* Add member section */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
          <input
            className="p-3 border rounded-xl"
            placeholder="Roll Number"
            value={member.roll}
            onChange={(e) => setMember((m) => ({ ...m, roll: e.target.value }))}
          />
          <input
            className="p-3 border rounded-xl"
            placeholder="Full Name"
            value={member.n}
            onChange={(e) => setMember((m) => ({ ...m, n: e.target.value }))}
          />
          <input
            className="p-3 border rounded-xl"
            placeholder="Branch"
            value={member.b}
            onChange={(e) => setMember((m) => ({ ...m, b: e.target.value }))}
          />
          <select
            className="p-3 border rounded-xl"
            value={member.s}
            onChange={(e) => setMember((m) => ({ ...m, s: e.target.value }))}
          >
            <option value="">Section</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
          </select>
          <input
            className="p-3 border rounded-xl"
            placeholder="Mobile Number"
            value={member.m}
            onChange={(e) => setMember((m) => ({ ...m, m: e.target.value }))}
            maxLength={10}
          />
          {/* Toggle for NOC */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={member.noc}
              onChange={(e) =>
                setMember((m) => ({ ...m, noc: e.target.checked }))
              }
            />
            NOC Submitted
          </label>
        </div>

        <button
          onClick={addMember}
          className="px-4 py-3 rounded-xl border hover:bg-gray-50"
        >
          Add Member
        </button>
      </div>

      {/* List of teams */}
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="font-semibold mb-3">Your Teams</h2>
        <div className="space-y-3">
          {teams.map((t) => (
            <div key={t.id} className="p-4 rounded-xl border">
              <div className="font-semibold">
                {t.n} <span className="text-gray-500">({t.id})</span>
              </div>
              {t.room && t.tb && (
                <div className="text-sm text-gray-600 mt-1">
                  Assigned: {t.room} / Table {t.tb}
                </div>
              )}
              <div className="mt-2 text-sm">
                <div className="font-medium">Members</div>
                <ul className="list-disc pl-5">
                  {t.m ? (
                    Object.entries(t.m).map(([r, m]) => (
                      <li key={r}>
                        {r.toLowerCase()} — {m.n}
                        {m.b ? ` • ${m.b}` : ""} {m.s ? ` • ${m.s}` : ""}
                        {m.m ? ` • 📱 ${m.m}` : ""}
                        {m.noc ? " • ✅ NOC Submitted" : " • ❌ NOC Pending"}
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500">No members yet.</li>
                  )}
                </ul>
              </div>
            </div>
          ))}
          {teams.length === 0 && (
            <div className="text-gray-500">No teams created yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
